import React, { lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { defineMessages, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Row, Col, Button, Container } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFacebook as fabFaFacebook,
  faXTwitter as fabFaXTwitter,
  faDiscord as fabFaDiscord,
  faSteam as fabFaSteam,
  faYoutube as fabFaYoutube,
  faInstagram as fabFaInstagram,
  faRedditAlien as fabFaRedditAlien,
} from '@fortawesome/free-brands-svg-icons';

import 'Components/Community/Community.scss';

import { fetchTopSellersSection, fetchBundleWithMysterySection } from 'Redux/ducks/algolia-calls';
import Meta from 'Components/Utility/SEO/Meta';
import SplashHeader from 'Components/Banner/SplashHeader';
import RazerCortextBanner from 'Components/Banner/RazerCortexCommunityBanner/RazerCortexCommunityBanner';
import getUrlParameter from 'Helpers/Url/GetUrlParameter';
import ProductCarouselSection from 'Containers/HomePage/ProductCarouselSection';
import EmailSignupPlacement from 'Components/Banner/EmailSignupPlacement/EmailSignupPlacement';

const CommunityBlogPostsContainer = lazy(() => import(/* webpackChunkName: "BlogPosts" */ 'Containers/Community/CommunityBlogPostsContainer'));

const messages = defineMessages({
  title: {
    id: 'community.title',
    defaultMessage: 'Community',
  },
  meta_description: {
    id: 'community.meta_description',
    defaultMessage: 'Join over 2.6 million Fanatical fans and start a conversation with our friendly community of like-minded gamers on Discord, Twitch, Facebook, Twitter, Reddit, and our Steam Community.',
  },
  splash_header_title: {
    id: 'community.spash_header.title',
    defaultMessage: 'Join our Community',
  },
  splash_header_subtitle: {
    id: 'community.spash_header.subtitle',
    defaultMessage: 'For exclusive news & offers',
  },
  topSellers: {
    id: 'community.topSellers',
    defaultMessage: 'Top Sellers',
  },
  bundles: {
    id: 'community.bundles',
    defaultMessage: 'Best Selling Bundles',
  },
});

export default function Community(props) {
  const { formatMessage } = props.intl;
  const emailNewsletter = useSelector(state => state?.auth?.email_newsletter);
  const razerSignup = getUrlParameter('signup') === 'razer';

  return (
    <main className="community">
      <Meta
        brandTitle
        title={formatMessage(messages.title)}
        description={formatMessage(messages.meta_description)}
        url="community"
      />
      <SplashHeader
        title={formatMessage(messages.splash_header_title)}
        subtitle={formatMessage(messages.splash_header_subtitle)}
      />

      <Container fluid className="p-0 community__content">
        {razerSignup &&
          <Container>
            <RazerCortextBanner />
          </Container>
        }

        <EmailSignupPlacement needsContainer appearsOn="community" />

        {!emailNewsletter &&
          <Container className="community-title">
            <Row>
              <Col className="pt-2" md={6}>
                <h3>
                  <FormattedMessage id="community.title.newsletter1" defaultMessage="Be the first to know" />
                </h3>
                <p className="text-center">
                  <FormattedMessage id="community.newsletter.text1" defaultMessage="Get emails about our latest bundles, sales & more!" />
                </p>
                <div className="brand-line my-3 mx-auto" />
              </Col>
              <Col className="pt-2" md={6}>
                <h3>
                  <FormattedMessage id="community.title.newsletter2" defaultMessage="Email exclusive coupons" />
                </h3>
                <p className="text-center">
                  <FormattedMessage id="community.newsletter.text2" defaultMessage="Subscriber-only codes to use on our bundles and deals!" />
                </p>
                <div className="brand-line my-3 mx-auto" />
              </Col>
            </Row>
            <Row>
              <Col className="pt-2" md={6}>
                <h3>
                  <FormattedMessage id="community.title.newsletter3" defaultMessage="Personalised emails" />
                </h3>
                <p className="text-center">
                  <FormattedMessage id="community.newsletter.text3" defaultMessage="Tailored emails based on your purchases and Wishlist!" />
                </p>
                <div className="brand-line my-3 mx-auto d-md-none" />
              </Col>
              <Col className="pt-2" md={6}>
                <h3>
                  <FormattedMessage id="community.title.newsletter4" defaultMessage="Contests and Giveaways" />
                </h3>
                <p className="text-center">
                  <FormattedMessage id="community.newsletter.text4" defaultMessage="We'll let you know when we've got on-site contests or giveaways first!" />
                </p>
              </Col>
            </Row>
          </Container>
        }


        <ProductCarouselSection
          lightThemeOverride
          title={formatMessage(messages.topSellers)}
          viewAllLocation={'top-sellers'}
          fetchHits={() => fetchTopSellersSection(12, true)}
          viewAllTitle={formatMessage(messages.topSellers)}
          hitCardNameTag="h3"
        />


        <section className="container-fluid social-media py-6">
          <Container className="community-title mb-4">
            <h2>
              <FormattedMessage id="community.title.socialmedia" defaultMessage="Follow Fanatical on Social Media" />
            </h2>
            <div className="brand-line my-3 mx-auto" />
          </Container>
          <Container className="text-center">

            <p className="mb-4">
              <FormattedMessage
                id="community.socialmedia.body"
                defaultMessage="Join over 2.6 million Fanatical fans and start a conversation with our friendly community of like-minded gamers on Facebook, Twitter, Reddit, and our Steam Community. Following Fanatical via our social media channels means that you're the first to see exclusive giveaways and announcements on the latest Steam key deals."
              />
            </p>

            <div className="fan-grid-container social-icons-container mb-4">
              <div className="seven-column-row">
                <a href="https://www.reddit.com/r/fanatical/" target="_blank" rel="noopener noreferrer">
                  <div className="icon-box reddit">
                    <FontAwesomeIcon icon={fabFaRedditAlien} size="2x" title="Reddit" />
                  </div>
                </a>
              </div>
              <div className="seven-column-row">
                <a href="https://www.facebook.com/wearefanatical" target="_blank" rel="noopener noreferrer">
                  <div className="icon-box facebook">
                    <FontAwesomeIcon icon={fabFaFacebook} size="2x" title="Facebook" />
                  </div>
                </a>
              </div>
              <div className="seven-column-row">
                <a href="https://twitter.com/fanatical" target="_blank" rel="noopener noreferrer">
                  <div className="icon-box twitter">
                    <FontAwesomeIcon icon={fabFaXTwitter} size="2x" title="X" />
                  </div>
                </a>
              </div>
              <div className="seven-column-row">
                <a href="https://www.youtube.com/wearefanatical" target="_blank" rel="noopener noreferrer">
                  <div className="icon-box youtube">
                    <FontAwesomeIcon icon={fabFaYoutube} size="2x" title="YouTube" />
                  </div>
                </a>
              </div>
              <div className="seven-column-row">
                <a href=" https://www.instagram.com/wearefanatical/" target="_blank" rel="noopener noreferrer">
                  <div className="icon-box instagram">
                    <FontAwesomeIcon icon={fabFaInstagram} size="2x" title="Instagram" />
                  </div>
                </a>
              </div>
              <div className="seven-column-row">
                <a href="https://fant.cl/Discord" target="_blank" rel="noopener noreferrer">
                  <div className="icon-box discord">
                    <FontAwesomeIcon icon={fabFaDiscord} size="2x" title="Discord" />
                  </div>
                </a>
              </div>
              <div className="seven-column-row">
                <a href="https://steamcommunity.com/groups/wearefanatical" target="_blank" rel="noopener noreferrer">
                  <div className="icon-box steam">
                    <FontAwesomeIcon icon={fabFaSteam} size="2x" title="Steam" />
                  </div>
                </a>
              </div>
            </div>
          </Container>
        </section>


        <ProductCarouselSection
          lightThemeOverride
          title={formatMessage(messages.bundles)}
          viewAllLocation={'bundle'}
          fetchHits={fetchBundleWithMysterySection}
          viewAllTitle={formatMessage(messages.bundles)}
          hitCardNameTag="h3"
        />


        {window.locale === 'en' &&
          <section>
            <Container className="community-title">
              <h2>
                <FormattedMessage id="community.fanatical-blog" defaultMessage="Fanatical Blog" />
              </h2>
              <div className="brand-line my-3 mx-auto" />
            </Container>
            <Container className="pb-6 text-center">
              <p className="mb-4">
                <FormattedMessage
                  id="community.blog.body"
                  defaultMessage="Written by gamers, for gamers, get regular updates on the latest Steam PC gaming news with Fanatical Blog. Jam-packed with content goodness, our blog features contests and details on the latest sales, as well as interviews with some of the industry's top publishers and developers, and much more!"
                />
              </p>
              <div className="fanatical-blog">
                <Suspense fallback="div">
                  <CommunityBlogPostsContainer />
                </Suspense>
                <div className="d-flex justify-content-center">
                  <Button
                    color="primary"
                    tag={Link}
                    to="/blog"
                    className="blog-btn"
                  >
                    <FormattedMessage id="community.blog.button.viewmoreposts" defaultMessage="View more Blog posts" />
                  </Button>
                </div>
              </div>
            </Container>
          </section>
        }
      </Container>
    </main>
  );
}
